import { useCallback, useEffect, useRef, useState } from 'react';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useLocale, useT } from '../i18n/locale-context';
import { tenderPath } from '../lib/slug';
import { formatBudgetCompact, formatDeadline } from '../lib/format';
import { getSupplierContracts, type SupplierContractListRow } from '../lib/catalog-client';

const KICKER = 'text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft';

/**
 * Every contract of a supplier, newest first, each with its buyer, with
 * «Показать ещё» down to the first one — the Pro view of `/supplier/:tin`
 * (Igor, 2026-10-12). Before this the paid view listed no contracts at all,
 * while the base held every row: a customer set our page beside a competitor's
 * «ещё 839» and asked where the rest were.
 *
 * Pro only. The public page keeps its own newest-8 window: «who won and at what
 * price» is the wall, and a free paged list of every award would walk around it.
 *
 * A failed page leaves the button in place: pressing it again retries. That is
 * also what a web release one deploy ahead of its API looks like — the section
 * simply does not appear until the endpoint exists.
 */
export function SupplierContracts({ tin }: { tin: string }) {
  const t = useT();
  const { locale } = useLocale();
  const [rows, setRows] = useState<SupplierContractListRow[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  // A double tap must not fetch the same page twice and append it twice.
  const busy = useRef(false);

  const loadMore = useCallback(
    async (offset: number) => {
      if (busy.current) return;
      busy.current = true;
      setLoading(true);
      try {
        const page = await getSupplierContracts(tin, offset);
        if (page.locked) {
          setNextOffset(null);
          return;
        }
        setRows((prev) => [...prev, ...page.rows]);
        setNextOffset(page.nextOffset);
      } catch {
        // Keep `nextOffset` — the button stays and the next press retries.
      } finally {
        busy.current = false;
        setLoading(false);
      }
    },
    [tin],
  );

  useEffect(() => {
    void loadMore(0);
  }, [loadMore]);

  if (rows.length === 0) return null;
  const unit = t('tender.budget.unit');
  return (
    <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.allContracts')}</div>
      <ul className="divide-y divide-edge-light">
        {rows.map((c, i) => {
          // A row with neither a lot nor a subject is named by its buyer.
          const label = c.title ?? c.tenderId ?? c.buyerName ?? '—';
          return (
            <li key={`${c.tenderId ?? 'row'}-${i}`} className="py-2.5 first:pt-0">
              <div className="flex items-baseline justify-between gap-3">
                {c.tenderId ? (
                  <Link
                    // Canonical, not the bare id — see the buyer page's note.
                    to={tenderPath(c.slug, c.tenderId)}
                    className="min-w-0 truncate text-[14px] font-semibold text-brand hover:underline"
                    title={label}
                  >
                    {label}
                  </Link>
                ) : (
                  <span
                    className="min-w-0 truncate text-[14px] font-semibold text-ink"
                    title={label}
                  >
                    {label}
                  </span>
                )}
                <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink-soft">
                  {formatBudgetCompact(String(Math.round(c.valueUzs)), locale) ?? '0'} {unit}
                </span>
              </div>
              {(c.awardedAt || c.buyerName) && (
                <div className="mt-0.5 flex min-w-0 gap-1.5 text-[12px] text-ink-muted">
                  {c.awardedAt && (
                    <span className="shrink-0">{formatDeadline(c.awardedAt, locale) ?? ''}</span>
                  )}
                  {c.awardedAt && c.buyerName && <span aria-hidden="true">·</span>}
                  {c.buyerName &&
                    (c.buyerTin ? (
                      <Link
                        to={`/buyer/${encodeURIComponent(c.buyerTin)}`}
                        className="min-w-0 truncate hover:text-brand hover:underline"
                        title={c.buyerName}
                      >
                        {c.buyerName}
                      </Link>
                    ) : (
                      <span className="min-w-0 truncate" title={c.buyerName}>
                        {c.buyerName}
                      </span>
                    ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {nextOffset != null && (
        <button
          type="button"
          onClick={() => void loadMore(nextOffset)}
          disabled={loading}
          className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-md border border-edge text-[13px] font-semibold text-ink-soft transition-colors hover:border-brand hover:text-brand disabled:opacity-60"
        >
          {t('supplierProfile.contractsMore')}
        </button>
      )}
    </section>
  );
}
